import useBusinessTravellerSocieteDetailsFetcher from 'businessTraveller/hooks/useBusinessTravellerSocieteDetailsFetcher'
import useSocieteCampaignTargetTier from 'loyalty/hooks/useSocieteCampaignTargetTier'
import { isAtOrAboveLoyaltyTier } from 'loyalty/utils/isAtOrAboveLoyaltyTier'
import useEmployeeOrders from 'businessTraveller/hooks/useEmployeeOrders'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import useOnboardingStatusResolved from 'businessTraveller/hooks/useOnboardingStatusResolved'

/**
 * Determines the active Société notification and returns the active notification type
 *
 * A notification is only eligible when ALL of the following base conditions are met:
 * - All required data has finished fetching (business account, business settings, linked credit account, employee notification)
 * - The `lebtSociete` feature flag is enabled
 * - The Société tier setting is enabled in business settings
 * - On the landing page: the onboarding modal has been dismissed or completed. This does not apply
 *   in the white label app, where the onboarding banner and modal are never shown
 *
 * Individual notification conditions (evaluated in priority order):
 *
 * 1. **Completion**
 *    - The employee is at the target tier or above
 *    - The employee was recently granted the target tier (15 days)
 *    - The Completion notification has not been dismissed yet
 *
 * 2. **Almost There**
 *    - The employee is below the target tier
 *    - The employee's completed qualifying bookings equal `businessSettings.threshold - 1` (one booking away from the target tier)
 *    - The higher-priority Completion notification has not been dismissed yet
 *    - The Almost There notification has not been dismissed yet
 *
 * 3. **Unlock Tier** — shown when the base conditions are met and the notification
 *    - The employee is below the target tier
 *    - The higher-priority Completion notification has not been dismissed yet
 *    - The higher-priority Almost There notification has not been dismissed
 *    - The Unlock notification has not been dismissed yet
 */

function useSocieteNotificationFetcher(): App.BusinessTraveller.SocieteNotificationType | null {
  const isSocieteEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.lebtSociete)
  const { targetTier } = useSocieteCampaignTargetTier()
  // Fetch the Société details including the tier setting, linked credit account, and employee notification
  const { isFetching, societeTierSetting, linkedCreditAccount, employeeNotification, employeeSociete } = useBusinessTravellerSocieteDetailsFetcher(targetTier)

  // Fetch the employee's completed qualifying bookings
  const { total: qualifiedBookings, isLoading: isLoadingOrders } = useEmployeeOrders({
    timeframe: 'qualificationWindow',
    page: 0,
    limit: 1,
    filterBy: 'traveller',
  })

  // Onboarding status should be resolved before showing societe notification on landing page
  const isOnboardingStatusResolved = useOnboardingStatusResolved()

  // Check if the employee's current loyalty tier is above or equal to the target tier
  const isAtOrAboveTargetTier = isAtOrAboveLoyaltyTier(linkedCreditAccount?.tier, targetTier)

  // Check if all required data has finished fetching
  const isDataFetched = !isFetching && !isLoadingOrders

  // Base conditions shared by all notifications
  const shouldShowSocieteNotification = isDataFetched && isSocieteEnabled && !!societeTierSetting?.enabled && isOnboardingStatusResolved

  /**
   * Check if the Almost There notification is eligible
   * - The Société notification should be shown
   * - The employee is below the target tier
   * - The employee's completed qualifying bookings equal `businessSettings.threshold - 1` (one booking away from the target tier)
   * - The Almost There notification has not been dismissed yet
   */
  const isAlmostThereEligible =
    shouldShowSocieteNotification
    && !isAtOrAboveTargetTier
    && (societeTierSetting?.threshold !== undefined && qualifiedBookings === societeTierSetting.threshold - 1)
    && !employeeNotification?.societe?.almostThereDismissed
    && !employeeNotification?.societe?.completionDismissed

  /**
   * Check if the Unlock notification is eligible
   * - The Société notification should be shown
   * - The employee is below the target tier
   * - The Unlock notification has not been dismissed yet
   * - The higher-priority Almost There notification has not been dismissed
   */
  const isUnlockEligible =
    shouldShowSocieteNotification
    && !isAtOrAboveTargetTier
    && !employeeNotification?.societe?.unlockTierDismissed
    && !employeeNotification?.societe?.almostThereDismissed
    && !employeeNotification?.societe?.completionDismissed

  /**
   * Check if the Completion notification is eligible
   * - The Société notification should be shown
   * - The employee is at the target tier or above
   * - The employee was recently granted the target tier
   * - The Completion notification has not been dismissed yet
   */
  const isCompletionEligible =
    shouldShowSocieteNotification
    && isAtOrAboveTargetTier
    && !!employeeSociete?.isSocieteRecentlyGranted
    && !employeeNotification?.societe?.completionDismissed

  /**
   * Determine the active notification based on the eligibility
   * Priority: Completion > AlmostThere > Unlock — only one shows at a time
   */
  if (isCompletionEligible) return 'COMPLETION'
  if (isAlmostThereEligible) return 'ALMOST_THERE'
  if (isUnlockEligible) return 'UNLOCK'
  return null
}

export default useSocieteNotificationFetcher
